/**
 * TrajectoriesPage (TRA-939) — every RUN in the window, one row per run.
 *
 * A run is the unit the review UI renders: a conversation (all traces
 * sharing a conversation id) or a conversation-less trace whose single
 * request still carries the full multi-turn history. Membership comes
 * from the gateway's audit log via GET /api/trajectories — tags and
 * review status are optional adornments, not the ticket of entry.
 *
 * The Flag column carries the run's verdict and the comment behind it, and
 * opens the editor that changes, dismisses or removes that flag in place
 * (TRA-963) — organizing benchmark feedback should not mean walking every
 * run through the review page again.
 *
 * Every control runs server-side (TRA-1091): search, the tag AND-match,
 * the status filter, the metadata filters (model, tenant, environment,
 * traffic, initiator), column sorting and paging all apply to the whole
 * window, so a page is one slice of one ordered list. The metadata
 * dropdowns offer the values seen on the current page plus whatever the
 * URL already selects. Every control is URL-backed so a view survives
 * reload and pastes into Slack.
 */

import { AlertTriangle, ExternalLink, Upload } from 'lucide-react'
import { useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  ListTrajectoriesApiTrajectoriesGetRange,
  type ListTrajectoriesApiTrajectoriesGetSort,
} from '@/api/generated/model'
import { ActiveFilters, FilterPopover } from '@/components/listing/filters'
import {
  activeFilterCount,
  metadataFilters,
  metadataQuery,
  plainFilterValue,
  type MetadataFilterSpec,
  type SortState,
} from '@/components/listing/listingParams'
import { SortHeader } from '@/components/listing/SortHeader'
import { useListingParams } from '@/components/listing/useListingParams'
import { reviewUrl } from '@/components/review/adHocItem'
import { rowVerdict } from '@/components/review/flagSource'
import { conversationPath, useConversationLinkState } from '@/components/traces/conversationLink'
import { VerdictChip } from '@/components/review/ReviewQueueSidebar'
import {
  evalDiverged,
  evalVerdict,
  flagComment,
  flagState,
  type FlagState,
} from '@/components/trajectories/flag'
import { FlagEditor } from '@/components/trajectories/FlagEditor'
import { LangsmithImportDialog } from '@/components/trajectories/LangsmithImportDialog'
import { langsmithTraceIdOf } from '@/components/trajectories/langsmithImport'
import { SideScrollArrows } from '@/components/trajectories/SideScrollArrows'
import { TagEditor } from '@/components/trajectories/TagEditor'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { CopyButton } from '@/components/ui/copy-button'
import { Input } from '@/components/ui/input'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Pagination } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTagVocabulary, useTrajectories } from '@/hooks/useLabels'
import type { RunListItem } from '@/hooks/useLabels'

type RangeValue = ListTrajectoriesApiTrajectoriesGetRange
const RANGE_OPTIONS = Object.values(ListTrajectoriesApiTrajectoriesGetRange)
const DEFAULT_RANGE: RangeValue = '7d'
// Fixed page size; make it a URL param if anyone asks.
const PAGE_SIZE = 50
// Placeholder rows on first load: enough to hold the table's shape.
const SKELETON_ROWS = Array.from({ length: 12 }, (_, i) => i)
const RANGE_LABELS: Record<RangeValue, string> = {
  '1h': 'Last hour',
  '24h': 'Last 24h',
  '48h': 'Last 48h',
  '5d': 'Last 5 days',
  '7d': 'Last 7 days',
  '14d': 'Last 14 days',
  '30d': 'Last 30 days',
  all: 'All time',
}

type StatusFilter = 'all' | 'unreviewed' | 'reviewed' | 'dismissed' | 'tagged'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'tagged', label: 'Tagged' },
]

// The Flag cell's state chip. 'none' is a run nobody touched: no chip at
// all, so the column stays quiet on ordinary traffic.
const FLAG_BADGE: Record<
  FlagState,
  { label: string; variant: 'destructive' | 'success' | 'secondary' } | null
> = {
  unreviewed: { label: 'Unreviewed', variant: 'destructive' },
  // Parked over the nightly review budget (TRA-1057) — waiting, not queued.
  held: { label: 'Held', variant: 'secondary' },
  reviewed: { label: 'Reviewed', variant: 'success' },
  dismissed: { label: 'Dismissed', variant: 'secondary' },
  tagged: { label: 'Tagged', variant: 'secondary' },
  none: null,
}

/** Metadata dropdown filters: one URL param (also the server's query
    param) + one accessor for the values a page shows.

    `model` matches ANY of the run's models (a conversation can span
    several); the rest are the client headers the gateway recorded. */
const METADATA_FILTERS: MetadataFilterSpec<RunListItem>[] = [
  { param: 'model', label: 'Model', values: (i) => i.models.map((m) => ({ value: m, label: m })) },
  {
    param: 'tenant',
    label: 'Tenant',
    values: (i) =>
      i.client_tenant_id === null
        ? []
        : [{ value: i.client_tenant_id, label: i.client_tenant_name ?? i.client_tenant_id }],
  },
  { param: 'environment', label: 'Environment', values: (i) => plainFilterValue(i.environment) },
  { param: 'traffic', label: 'Traffic', values: (i) => plainFilterValue(i.traffic_type) },
  { param: 'session', label: 'Session', values: (i) => plainFilterValue(i.session_id) },
  { param: 'initiator', label: 'Initiated by', values: (i) => plainFilterValue(i.initiated_by) },
]
const METADATA_PARAMS = METADATA_FILTERS.map((f) => f.param)

/** The client headers the gateway recorded, one column each (TRA-912,
    TRA-1149). The name column is what Cartage sends as X-Tenant-Name; the
    id column is X-Tenant-Id. */
interface MetaColumn {
  label: string
  value: (item: RunListItem) => string | null
  /** Show the whole value with a copy button instead of truncating it. */
  copyable?: boolean
}
// Three groups because the sortable columns sit between them: the tenant
// name leads, the ids follow Turns, and the low-signal headers trail Run.
const TENANT_COLUMN: MetaColumn = { label: 'Tenant', value: (i) => i.client_tenant_name }
const ID_COLUMNS: MetaColumn[] = [
  { label: 'Env', value: (i) => i.environment },
  { label: 'LangSmith run', value: (i) => i.langsmith_run_id, copyable: true },
  { label: 'Tenant id', value: (i) => i.client_tenant_id, copyable: true },
  // The id review links carry (`/review?item=`), searchable since PR #493
  // but not visible before TRA-1168. Same choice as `reviewLink`: the
  // flag's trace when the run is flagged, else the representative trace.
  { label: 'Trace id', value: (i) => i.flag?.trace_id ?? i.trace_id, copyable: true },
]
const TRAIL_COLUMNS: MetaColumn[] = [
  { label: 'Traffic', value: (i) => i.traffic_type },
  { label: 'Session', value: (i) => i.session_id },
  { label: 'Initiated by', value: (i) => i.initiated_by },
]

type SortField = ListTrajectoriesApiTrajectoriesGetSort
const SORT_FIELDS: SortField[] = ['run', 'model', 'turns', 'tags', 'flag', 'last']
const DEFAULT_SORT: SortState<SortField> = { field: 'last', desc: true }
const LISTING = {
  ranges: RANGE_OPTIONS,
  defaultRange: DEFAULT_RANGE,
  sortFields: SORT_FIELDS,
  defaultSort: DEFAULT_SORT,
  metadataParams: METADATA_PARAMS,
}

function transcriptUrl(item: RunListItem): string | null {
  // A conversation opens the whole run by id; a single run opens by full
  // R2 key through the same server-side trajectory assembly the review
  // page uses. A single run without a key would be a server bug —
  // the listing skips body-less log rows — so say so rather than 404.
  if (item.kind === 'conversation') {
    return conversationPath(item.run_id)
  }
  if (item.trace_key === null) {
    console.error(`Trajectories: single run ${item.run_id} has no trace_key`)
    return null
  }
  return `/conversations?trace_key=${encodeURIComponent(item.trace_key)}`
}

/**
 * Where the row's "Review" link goes (TRA-1079). A flag can sit on a
 * different turn than the run's representative trace, and the review page
 * keys on the FLAG's trace — so the flag wins when there is one. With no
 * flag the representative trace goes, plus its key so the review page can
 * open a run the queue does not hold. Null only when the run has no trace
 * in the window at all — nothing to review then.
 */
function reviewLink(item: RunListItem): string | null {
  if (item.flag !== null) return reviewUrl(item.flag.trace_id, item.flag.trace_key)
  if (item.trace_id === null || item.trace_key === null) return null
  return reviewUrl(item.trace_id, item.trace_key)
}

/** Static chips for a run whose tags can't be edited yet (no trace in view). */
function TagChips({ tags }: { tags: string[] }): React.JSX.Element {
  if (tags.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {tags.map((tag) => (
        <span
          key={tag}
          title={tag}
          className="inline-flex h-5 max-w-[200px] items-center whitespace-nowrap border border-border bg-muted px-1.5 font-mono text-[11px] text-foreground"
        >
          <span className="truncate">{tag}</span>
        </span>
      ))}
    </div>
  )
}

/**
 * The Flag cell's read side: state chip, verdict chip, and the comment
 * whoever flagged the run wrote (TRA-963). The comment is the point — a
 * bare "Reviewed" badge never said WHY a run was marked.
 */
/** One client-header column (TRA-912, TRA-1149). The em dash is the
    empty-cell placeholder. */
function MetaCell({ column, item }: { column: MetaColumn; item: RunListItem }): React.JSX.Element {
  const value = column.value(item)
  if (value === null) {
    return <TableCell className="text-xs text-muted-foreground">—</TableCell>
  }
  if (column.copyable === true) {
    return (
      <TableCell className="whitespace-nowrap font-mono text-xs">
        <span className="inline-flex items-center gap-1">
          {value}
          <CopyButton value={value} label={`Copy ${column.label}`} />
        </span>
      </TableCell>
    )
  }
  return (
    <TableCell className="max-w-[160px] font-mono text-xs">
      <span className="block truncate" title={value}>
        {value}
      </span>
    </TableCell>
  )
}

function SkeletonRow(): React.JSX.Element {
  return (
    <TableRow aria-hidden="true">
      <TableCell>
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-36" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-14" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-14" />
      </TableCell>
      <TableCell className="text-right">
        <Skeleton className="ml-auto h-4 w-6" />
      </TableCell>
      {ID_COLUMNS.map((column) => (
        <TableCell key={column.label}>
          <Skeleton className="h-4 w-20" />
        </TableCell>
      ))}
      <TableCell className="max-w-[340px]">
        <Skeleton className="h-4 w-48" />
      </TableCell>
      {TRAIL_COLUMNS.map((column) => (
        <TableCell key={column.label}>
          <Skeleton className="h-4 w-20" />
        </TableCell>
      ))}
      <TableCell className="text-right">
        <Skeleton className="ml-auto h-4 w-40" />
      </TableCell>
    </TableRow>
  )
}

function FlagSummary({ item }: { item: RunListItem }): React.JSX.Element {
  const badge = FLAG_BADGE[flagState(item)]
  const flag = item.flag
  const verdict = flag === null ? null : rowVerdict(flag)
  const comment = flag === null ? '' : flagComment(flag)
  // An untouched run gets the dashed opt-in chip the tag editor uses, so
  // the column reads as somewhere to record a verdict rather than a blank.
  // Without a trace to write to there is nothing to offer, so it stays "—".
  const flaggable = item.trace_id !== null && item.trace_key !== null
  return (
    <>
      <div className="flex items-center gap-1">
        {badge === null ? (
          flaggable ? (
            <span className="inline-flex h-5 items-center border border-dashed border-border px-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:border-muted-foreground hover:text-foreground">
              Flag
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )
        ) : (
          <Badge variant={badge.variant}>{badge.label}</Badge>
        )}
        {verdict !== null && <VerdictChip {...verdict} />}
        {flag !== null && evalDiverged(flag) && (
          <span
            className="inline-flex items-center text-destructive"
            title={`The eval built from this run grades it ${evalVerdict(flag)}`}
          >
            <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5" />
            <span className="sr-only">
              Verdict no longer matches the eval built from this run
            </span>
          </span>
        )}
      </div>
      {comment !== '' && (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <span className="block max-w-[240px] truncate text-xs text-muted-foreground">
              {comment}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="max-w-md whitespace-pre-wrap">
            {comment}
          </TooltipContent>
        </Tooltip>
      )}
    </>
  )
}

interface TrajectoryRowProps {
  item: RunListItem
}

function TrajectoryRow({ item }: TrajectoryRowProps): React.JSX.Element {
  const navigate = useNavigate()
  const url = transcriptUrl(item)
  const review = reviewLink(item)
  // The Conversation page's back arrow returns here, filters and all.
  const linkState = useConversationLinkState('trajectories')
  return (
    <TableRow
      className={url === null ? undefined : 'cursor-pointer'}
      onClick={() => {
        if (url !== null) navigate(url, { state: linkState })
      }}
    >
      <MetaCell column={TENANT_COLUMN} item={item} />
      <TableCell className="font-mono text-xs text-muted-foreground">
        {item.models.length === 0 ? '—' : item.models.join(', ')}
      </TableCell>
      <TableCell
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        {item.trace_id !== null && item.trace_key !== null ? (
          <TagEditor
            traceId={item.trace_id}
            traceKey={item.trace_key}
            {...(item.kind === 'conversation'
              ? { conversationId: item.run_id }
              : {})}
            tags={item.tags}
          />
        ) : (
          // A conversation whose turns all fell outside the scanned log
          // window has no trace to hang a tag on — chips only.
          <TagChips tags={item.tags} />
        )}
      </TableCell>
      <TableCell
        className="max-w-[260px]"
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        {/* Keyed on the flag's own updated_at so the editor's draft resets
            once a save round-trips — otherwise a reopened popover would
            still hold the text it was opened with. */}
        <div className="flex items-start gap-2">
          <div className="min-w-0">
            <FlagEditor
              key={item.flag === null ? 'unflagged' : item.flag.updated_at}
              flag={item.flag}
              traceId={item.trace_id}
              traceKey={item.trace_key}
            >
              <FlagSummary item={item} />
            </FlagEditor>
          </div>
          {/* One click to the review page, queue positioned on this run
              (TRA-1079) — no more copying the id across. A run with no
              flag opens there too, so the verdict can be written with the
              full transcript and the ask panel at hand. */}
          {review !== null && (
            <Link
              to={review}
              title="Open this run in the review page"
              aria-label="Open in review"
              className="mt-0.5 inline-flex flex-none items-center gap-0.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              Review
              <ExternalLink aria-hidden="true" className="h-3 w-3" />
            </Link>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
        {item.turn_count}
      </TableCell>
      {ID_COLUMNS.map((column) => (
        <MetaCell key={column.label} column={column} item={item} />
      ))}
      <TableCell className="max-w-[340px]">
        {url === null ? (
          <span className="font-mono text-xs">{item.run_id}</span>
        ) : (
          <Link
            to={url}
            state={linkState}
            className="hover:underline"
            onClick={(event) => {
              event.stopPropagation()
            }}
          >
            {item.preview === null ? (
              <span className="font-mono text-xs">{item.run_id}</span>
            ) : (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className="block truncate text-xs">{item.preview}</span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="max-w-md whitespace-pre-wrap">
                  {item.preview}
                </TooltipContent>
              </Tooltip>
            )}
          </Link>
        )}
      </TableCell>
      {TRAIL_COLUMNS.map((column) => (
        <MetaCell key={column.label} column={column} item={item} />
      ))}
      <TableCell className="whitespace-nowrap text-right font-mono text-xs tabular-nums text-muted-foreground">
        {new Date(item.last_seen).toLocaleString()}
      </TableCell>
    </TableRow>
  )
}

export default function TrajectoriesPage(): React.JSX.Element {
  const listing = useListingParams(LISTING)
  const { searchParams, updateParams, debouncedSearch, idSearch, effectiveRange, sort, page } =
    listing
  // Validate against the known options: a mistyped param in a shared link
  // must degrade to the default, not 422 the request and blank the page.
  const statusParam = searchParams.get('status')
  const status: StatusFilter = STATUS_OPTIONS.some((o) => o.value === statusParam)
    ? (statusParam as StatusFilter)
    : 'all'
  const selectedTags = searchParams.getAll('tags')
  const tableScrollRef = useRef<HTMLDivElement>(null)

  // Metadata filters: the URL param name is the server's query param name.
  const metadata = metadataQuery(searchParams, METADATA_PARAMS)
  const metadataSelected = Object.keys(metadata).length > 0
  // The upload door shows only when the id alone came up empty: with a tag,
  // flag or metadata filter on top, the run may well exist and be filtered
  // out, so that miss keeps the plain message.
  // A trace key holds the range too (idSearch) but names a platform trace,
  // never a LangSmith run, so the door is for a UUID or LangSmith link
  // alone. A link gives the door its trace id, not the link as typed.
  const idSearchedAlone =
    selectedTags.length === 0 && status === 'all' && !metadataSelected
      ? langsmithTraceIdOf(debouncedSearch)
      : null

  const vocabulary = useTagVocabulary()
  const trajectories = useTrajectories({
    range: effectiveRange,
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
    ...(selectedTags.length > 0 ? { tags: selectedTags } : {}),
    ...(status !== 'all' ? { status } : {}),
    ...metadata,
    sort: sort.field,
    dir: sort.desc ? 'desc' : 'asc',
    offset: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  })

  function toggleTag(tag: string): void {
    updateParams((params) => {
      const current = params.getAll('tags')
      params.delete('tags')
      const next = current.includes(tag)
        ? current.filter((t) => t !== tag)
        : [...current, tag]
      for (const t of next) params.append('tags', t)
    })
  }

  // Clears the popover's filters only. Search, range and flag stay: they
  // live in the bar, so the user can see and change them directly.
  function clearFilters(): void {
    updateParams((params) => {
      params.delete('tags')
      for (const param of METADATA_PARAMS) params.delete(param)
    })
  }

  function setStatus(value: StatusFilter): void {
    updateParams((params) => {
      if (value === 'all') params.delete('status')
      else params.set('status', value)
    })
  }

  const items = trajectories.data?.items ?? []
  const total = trajectories.data?.total ?? 0
  // An empty previous result kept as placeholder is not this query's
  // answer: show the skeleton, not "no runs match", until it lands.
  const loading =
    trajectories.isLoading || (trajectories.isPlaceholderData && items.length === 0)
  const pageCount = Math.ceil(total / PAGE_SIZE)
  // Dropdown options are the distinct values on this page.
  const metadataOptions = metadataFilters(METADATA_FILTERS, searchParams, items)
  const activeCount = activeFilterCount(metadataOptions, selectedTags)
  const tagFilters = { tags: vocabulary.data?.tags ?? [], selectedTags, onToggleTag: toggleTag }

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="Trajectories"
        description="See reviewed and unreviewed trajectories from production traffic."
      />

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
          <Input
            value={listing.search}
            onChange={(event) => {
              listing.setSearch(event.target.value)
            }}
            placeholder="Search any column, tags, notes…"
            aria-label="Search trajectories"
            className="h-8 w-[420px] font-mono text-xs"
          />
          <Select
            value={effectiveRange}
            disabled={idSearch}
            onValueChange={(value) => {
              listing.setRange(value as RangeValue)
            }}
          >
            <SelectTrigger
              aria-label="Time range"
              title={idSearch ? 'An id search covers all time.' : undefined}
              className="h-8 w-auto min-w-[130px] gap-1 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((option) => (
                <SelectItem key={option} value={option} className="text-xs">
                  {RANGE_LABELS[option]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={status}
            onValueChange={(value) => {
              setStatus(value as StatusFilter)
            }}
          >
            <SelectTrigger
              aria-label="Flag filter"
              className="h-8 w-auto min-w-[110px] gap-1 text-xs"
            >
              <span className="text-muted-foreground">Flag:</span>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-xs">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FilterPopover
            filters={metadataOptions}
            onMetadata={listing.setMetadata}
            tagFilters={tagFilters}
            emptyMessage="No filters apply to these runs."
          />
        </div>
        {activeCount > 0 && (
          <ActiveFilters
            filters={metadataOptions}
            onMetadata={listing.setMetadata}
            tagFilters={tagFilters}
            onClear={clearFilters}
          />
        )}
      </div>

      {trajectories.isError ? (
        <p className="border border-dashed border-destructive/40 p-6 text-sm text-destructive">
          The trajectories list could not be loaded. Reload to retry.
        </p>
      ) : !loading &&
        items.length === 0 &&
        idSearchedAlone !== null &&
        !(page > pageCount && pageCount > 0) ? (
        // A LangSmith run id with no match is a run from before the client
        // sent the id header (TRA-1087), or a step id from inside a trace,
        // which the client never sends (TRA-1541): the empty state says
        // what does find a run and IS the upload door (TRA-1169). A plain
        // word or a wider filter set is just a miss.
        <EmptyState
          icon={Upload}
          title="No runs match this id"
          description="No run carries this id. If it came from LangSmith, paste the whole link or the id after /trace/. You can also add the run's LangSmith export below."
          action={<LangsmithImportDialog runId={idSearchedAlone} />}
        />
      ) : !loading && items.length === 0 ? (
        <div className="space-y-2">
          <p className="border border-dashed border-border p-6 text-sm text-muted-foreground">
            {page > pageCount && pageCount > 0
              ? 'This page is past the end of the list.'
              : debouncedSearch || selectedTags.length > 0 || status !== 'all' || metadataSelected
                ? 'No runs match these filters.'
                : 'No runs in this window. Widen the time range, or send some traffic through the gateway.'}
          </p>
          {page > 1 && (
            <Pagination
              currentPage={page}
              totalPages={pageCount}
              onPrevious={() => {
                listing.setPage(page - 1)
              }}
              onNext={() => {
                listing.setPage(page + 1)
              }}
            />
          )}
        </div>
      ) : (
        <div className="space-y-2" aria-busy={trajectories.isFetching}>
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            {loading ? 'Loading runs' : `${String(total)} runs in this window`}
          </p>
          {/* `relative` anchors the floating scroll arrows to this box. The
              thirteen columns are wider than the page on most screens, so
              the table scrolls sideways inside it (TRA-1166). */}
          <div
            className={`relative border border-border motion-safe:transition-opacity ${
              trajectories.isFetching && !loading ? 'opacity-60' : ''
            }`}
          >
          <SideScrollArrows scrollRef={tableScrollRef} />
          <Table containerRef={tableScrollRef}>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">{TENANT_COLUMN.label}</TableHead>
                <SortHeader field="model" label="Model" sort={sort} onSort={listing.setSort} />
                <SortHeader field="tags" label="Tags" sort={sort} onSort={listing.setSort} />
                <SortHeader field="flag" label="Flag" sort={sort} onSort={listing.setSort} />
                <SortHeader
                  field="turns"
                  label="Turns"
                  sort={sort}
                  onSort={listing.setSort}
                  alignRight
                />
                {ID_COLUMNS.map((column) => (
                  <TableHead key={column.label} className="whitespace-nowrap">
                    {column.label}
                  </TableHead>
                ))}
                <SortHeader field="run" label="Run" sort={sort} onSort={listing.setSort} />
                {TRAIL_COLUMNS.map((column) => (
                  <TableHead key={column.label} className="whitespace-nowrap">
                    {column.label}
                  </TableHead>
                ))}
                <SortHeader
                  field="last"
                  label="Last activity"
                  sort={sort}
                  onSort={listing.setSort}
                  alignRight
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading
                ? SKELETON_ROWS.map((i) => <SkeletonRow key={i} />)
                : items.map((item) => (
                    <TrajectoryRow
                      key={`${item.kind}:${item.run_id}`}
                      item={item}
                    />
                  ))}
            </TableBody>
          </Table>
          </div>
          {pageCount > 1 && (
            <Pagination
              currentPage={page}
              totalPages={pageCount}
              onPrevious={() => {
                listing.setPage(page - 1)
              }}
              onNext={() => {
                listing.setPage(page + 1)
              }}
            />
          )}
        </div>
      )}
    </PageContainer>
  )
}
